// PostponedChecks.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Tooltip,
    TablePagination, Checkbox, TextField,
    CircularProgress, Box
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import dayjs from 'dayjs';
import DocumentService from '../../../Services/documents.service';
import EnumsService from '../../../Services/enums.service';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import Constants from '../../../assests/Constants/constants';
import PaymentAcceptanceCreateEditModal from '../../PaymentAcceptance/SubComponents/PaymentAcceptanceCreateEditModal';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useBack } from 'use-back';
import CustomDropdown from '../../CustomDropdown/CustomDropdown';
import TaxAuthorityService from '../../../Services/taxAuthority.service';
import { getIsraelInvoiceAmountLimit } from '../../../Utils/GetIsraelInvoiceAmountLimit';
import { getVatValue } from '../../../Utils/GetVatValue';
import ClientService from '../../../Services/client.service';
import ClientDocumentCountService from '../../../Services/ClientDocumentCount.service';
import ClientItemsService from '../../../Services/clientItems.service';
import { validatePaymentAcceptanceItems } from '../../../Utils/documentValidation';
import AddItemsModal from '../../TransactionDocuments/SubComponents/AddItemsModal.jsx';
import { useMemo } from 'react';
import TaxAuthorityErrorModal from '../../TaxAuthorityErrorModal/TaxAuthorityErrorModal.jsx';
import { parseServerError } from '../../../Utils/parseServerError';
import Swal from 'sweetalert2';
import fieldStyles from '../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import styles from './PostponedChecks.module.scss';


/* ---------- utils ---------- */
function base64ToBlobUrl(base64, mimeType = 'application/pdf') {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return URL.createObjectURL(blob);
}

const PostponedChecks = ({
    open = false,
    onClose = () => { },
    caseId = '',
    token = '',
    accountCardsOptionsFromParent = [],
}) => {
    const { t, i18n } = useTranslation();

    /* --------------- state --------------- */
    const [paymentAcceptances, setPaymentAcceptances] = useState([]);
    const [checks, setChecks] = useState([]);
    const [accountCards, setAccountCards] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedCheckIds, setSelectedCheckIds] = useState([]);
    const [notes, setNotes] = useState({});
    const [editingCheckId, setEditingCheckId] = useState(null);
    const [resumeInvoiceCreation, setResumeInvoiceCreation] = useState(false);


    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'paymentDate', direction: 'asc' });

    // dates
    const [startDate, setStartDate] = useState(null); // Default to null
    const [untilDate, setUntilDate] = useState(new Date());
    const [generateDate, setGenerateDate] = useState(new Date());
    const [isGeneratingDrafts, setIsGeneratingDrafts] = useState(false);
    const [isConfirmingFinals, setIsConfirmingFinals] = useState(false);


    // PDF preview
    const [pdfResults, setPdfResults] = useState([]);
    const [pdfIndex, setPdfIndex] = useState(0);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [blobUrl, setBlobUrl] = useState('');
    const [slideDirection, setSlideDirection] = useState(1);
    const [pendingPairs, setPendingPairs] = useState([]);

    // Account cards phone checks
    const [missingPhonesModalOpen, setMissingPhonesModalOpen] = useState(false);
    const [missingPhones, setMissingPhones] = useState([]);
    const [phoneEdits, setPhoneEdits] = useState({});
    const [isTaxAuthorityErrorModalVisible, setIsTaxAuthorityErrorModalVisible] = useState(false);
    // The check whose invoice was rejected by the Tax Authority and currently
    // awaiting a per-invoice decision from the user.
    const [decisionCheck, setDecisionCheck] = useState(null);
    const decisionResolverRef = useRef(null);
    const [showAddItemsModal, setShowAddItemsModal] = useState(false);
    const [currentAddItemsDoc, setCurrentAddItemsDoc] = useState(null);
    const [clientItems, setClientItems] = useState([]);
    const [isSavingItems, setIsSavingItems] = useState(false);
    // When multiple checks are selected we collect items for each check in turn,
    // so every check still produces its own invoice with its own items.
    const [addItemsQueue, setAddItemsQueue] = useState([]);
    const [addItemsIndex, setAddItemsIndex] = useState(0);
    const [itemsByCheckId, setItemsByCheckId] = useState({});

    const [user, setUser] = useState(null);


    // rtl/ltr for inputs
    const [direction, setDirection] = useState('ltr');
    useEffect(() => {
        setDirection(i18n.language === 'he' || i18n.language === 'ar' ? 'rtl' : 'ltr');
    }, [i18n.language]);

    /* ---------- NEW: edit‑payment‑acceptance modal state ---------- */
    const [paModalOpen, setPaModalOpen] = useState(false);
    const [editablePA, setEditablePA] = useState(null);

    const searchInputProps = { disableUnderline: true };
    const datePickerInputSx = {
        border: '0 !important',
        borderRadius: '0 !important',
        background: 'transparent !important',
        height: '40px',
        minHeight: '40px',
        boxSizing: 'border-box',
        boxShadow: 'none !important',
        outline: 'none !important',
        padding: '0 8px',
        '&:hover, &:focus, &.Mui-focused, &:focus-within': {
            border: '0 !important',
            boxShadow: 'none !important',
        },
    };

    /* --------------- data fetch --------------- */
    useEffect(() => {
        if (open && caseId && token) {
            fetchPostponedChecks();
        }
    }, [open, caseId, token, untilDate, startDate]);

    const [manualChecks, setManualChecks] = useState(false);

    const handleChangeManualChecks = (isClosedManually) => {
        setManualChecks(isClosedManually);
        fetchPostponedChecks(isClosedManually);
    };
    const [loadingChecks, setLoadingChecks] = useState(false);


    const fetchPostponedChecks = async (isClosedManually = manualChecks) => {
        try {
            setLoadingChecks(true);

            // Set untilDate to end of UTC day for the API call
            const endOfUtcDay = new Date(Date.UTC(
                untilDate.getUTCFullYear(),
                untilDate.getUTCMonth(),
                untilDate.getUTCDate(),
                23, 59, 59, 999
            ));

            // Set startDate to start of UTC day for the API call, or null if not set
            let startOfUtcDay = null;
            if (startDate) {
                startOfUtcDay = new Date(Date.UTC(
                    startDate.getUTCFullYear(),
                    startDate.getUTCMonth(),
                    startDate.getUTCDate(),
                    0, 0, 0, 0
                ));
            }

            const data = await DocumentService.GetPostponedChecks(
                caseId,
                endOfUtcDay,
                startOfUtcDay ? startOfUtcDay : null,
                token,
                isClosedManually
            );
            setPaymentAcceptances(data?.paymentAcceptances || []);
            setChecks(data?.checks || []);
            setLoadingChecks(false);

        } catch (err) {
            setLoadingChecks(false);
            console.error('Error fetching postponed checks:', err);
            setPaymentAcceptances([]);
            setChecks([]);
        }
    };

    const handleCloseManually = async () => {
        try {
            if (!selectedCheckIds.length) {
                toast.info(t('NoChecksSelected'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                return;
            }
            await DocumentService.ManuallyCloseOrOpen(caseId, selectedCheckIds, true, token);
            toast.success(t('PostponedChecksClosed'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            setSelectedCheckIds([]);
            fetchPostponedChecks(manualChecks);
        } catch (err) {
            console.error('Error closing postponed checks:', err);
            toast.error(t('ClosePostponedChecksFailed'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
        }
    };

    const handleOpenManually = async () => {
        try {
            if (!selectedCheckIds.length) {
                toast.info(t('NoChecksSelected'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                return;
            }
            await DocumentService.ManuallyCloseOrOpen(caseId, selectedCheckIds, false, token);
            toast.success(t('PostponedChecksClosed'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            setSelectedCheckIds([]);
            fetchPostponedChecks(manualChecks);
        } catch (err) {
            console.error('Error reopening postponed checks:', err);
            toast.error(t('ClosePostponedChecksFailed'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
        }
    };

    // NEW: fetch the accountCards
    useEffect(() => {
        if (open && caseId && token) {
            fetchAccountCards();
            fetchClientItems();
        }
    }, [open, caseId, token]);

    const fetchAccountCards = async () => {
        try {
            const response = await EnumsService.GetAccountCardsByCaseId(caseId, token);
            if (response.ok) {
                const result = await response.json();
                setAccountCards(result);
            }
        } catch (err) {
            console.error('Error fetching account cards:', err);
            setAccountCards([]);
        }
    };

    const fetchClientItems = async () => {
        try {
            const response = await ClientItemsService.GetClientItemsByCaseId(caseId, token);
            if (!response.ok) {
                setClientItems([]);
                return;
            }
            const data = await response.json();
            const safeItems = Array.isArray(data) ? data : [];
            safeItems.unshift({ makat: 'add-item', name: t('NewItem') });
            setClientItems(safeItems);
        } catch (err) {
            console.error('Error fetching client items:', err);
            setClientItems([]);
        }
    };

    const closePaModal = () => {
        setPaModalOpen(false);
        setEditablePA(null);
        // Refresh list in case something changed
        fetchPostponedChecks(manualChecks);
    };

    const handleSavePaymentAcceptance = async () => {
        try {
            setModalVisible(false);
            setLoading(true);
            // Prepare the data to be sent in the request body
            const updatedDocument = {
                id: editablePA.id,
                caseId: editablePA.caseId,
                accountCodeName: editablePA.accountCodeName,
                accountCodeId: editablePA.accountCodeId,
                reference: editablePA.reference,
                amount: editablePA.amount,
                uploadDate: editablePA.uploadDate,
                paymentAcceptanceType: editablePA.paymentAcceptanceType,
                description: editablePA.description,
                withholdingTax: editablePA.withholdingTax,
                accountCaseId: editablePA.accountCaseId,
                checks: editablePA.checks,
                creditCards: editablePA.creditCards,
                cash: editablePA.cash,
                bankTransactions: editablePA.bankTransactions,
                date: editablePA.date ? new Date(editablePA.date).toISOString() : new Date().toISOString(),
                reportingMonthDate: new Date(editablePA.reportingMonthDate).toISOString(),
                confirmationNumber: editablePA.confirmationNumber,
                creditAccount: editablePA.creditAccount,
                creditAccountAmount: editablePA.creditAccountAmount,
                debitAccount: editablePA.debitAccount,
                debitAccountAmount: editablePA.debitAccountAmount,
            };


            // Call the UpdateDocument function from DocumentService
            const response = await DocumentService.UpdateDocument("PaymentAcceptances", token, updatedDocument);

            if (response.ok) {
                // Handle successful update
                if (editablePA.status === 'done') {
                    await handleDoneClick(editablePA.id);
                } else if (editablePA.status === 'notDone') {
                    await handleNotDoneClick(editablePA.id);
                }
                toast.success(t('documentUpdateSuccess'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            } else {
                // Handle any errors or display an error message
                toast.error(t('documentUpdateError'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            }
        } catch (error) {
            console.error('documentUpdateError:', error);
            // Show an error toast message
            toast.error(t('documentUpdateError'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
        }
        await onRefresh();
        handleClose(); // Function to revert or exit the edit mode
        setLoading(false);

    };

    /* --------------- invoice generation --------------- */
    // Opens the AddItemsModal for a single check in the queue. The modal is
    // remounted per check (via key) so its form starts fresh each time.
    const openAddItemsForCheck = (queue, index) => {
        const check = queue?.[index];
        if (!check) return;
        setCurrentAddItemsDoc({
            id: check.paymentAcceptanceId,
            checkId: check.checkId,
            amount: check.amount || 0,
            checkLabel: check.description || '',
            position: index + 1,
            total: queue.length
        });
        setShowAddItemsModal(true);
    };

    const resetAddItemsFlow = () => {
        setShowAddItemsModal(false);
        setAddItemsQueue([]);
        setAddItemsIndex(0);
        setItemsByCheckId({});
        setCurrentAddItemsDoc(null);
    };

    const handleGenerateInvoicesDrafts = async () => {
        try {
            setIsGeneratingDrafts(true);           // show loading on “Generate Invoices”

            // 1) basic guards
            if (!selectedCheckIds.length) {
                toast.info(t('NoChecksSelected'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                return;
            }
            if (!caseId || !token) return;

            // 2) build the pairs array (with note + accountCodeId)
            const pairs = selectedCheckIds
                .map(id => {
                    const ck = checks.find(ch => ch.checkId === id);
                    if (!ck) return null;
                    return {
                        paymentAcceptanceId: ck.paymentAcceptanceId,
                        checkId: ck.checkId,
                        note: notes[ck.checkId] || null,
                        accountCodeId: paymentAcceptances
                            .find(p => p.id === ck.paymentAcceptanceId).accountCodeId
                    };
                })
                .filter(Boolean);

            // 3) check for missing phone numbers on the relevant account‑cards
            const uniqueCodes = [
                ...new Set(
                    paymentAcceptances
                        .filter(pa => pa.checks && pa.checks.length > 0)
                        .map(pa => pa.accountCodeId)
                )
            ];

            const missing = uniqueCodes
                .map(acId => accountCards.find(a => a.cardId === acId))
                .filter(ac => ac && !ac.phoneNumber)
                .map(ac => ({ cardId: ac.cardId, name: ac.name || '', phoneNumber: '' }));

            if (missing.length > 0) {
                setMissingPhones(missing);
                setPhoneEdits(Object.fromEntries(missing.map(m => [m.cardId, ''])));
                setMissingPhonesModalOpen(true);
                return;
            }

            // 4) collect items per check (every check produces its own invoice),
            //    opening the AddItemsModal once for each selected check in turn
            const selectedChecks = checks.filter(ch => selectedCheckIds.includes(ch.checkId));
            if (!selectedChecks.length) {
                toast.info(t('SelectChecksToAddItems'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                return;
            }

            setPendingPairs(pairs); // remember for generation after items are collected
            setItemsByCheckId({});
            setAddItemsQueue(selectedChecks);
            setAddItemsIndex(0);
            openAddItemsForCheck(selectedChecks, 0);
        } catch (err) {
            console.error('Error generating invoices:', err);
            toast.error(t('GenerateInvoicesFailed'), { autoClose: 5000, position: direction === 'rtl' ? 'top-left' : 'top-right' });
        } finally {
            setIsGeneratingDrafts(false);          // hide loading
        }
    };

    const actuallyGenerateInvoicesDrafts = async (pairs) => {
        setPendingPairs(pairs);
        try {
            const resp = await DocumentService.GeneratePostponedChecksDrafts(
                caseId,
                pairs,
                generateDate.toISOString(),
                token
            );
            if (resp.mismatches?.length) {
                toast.warning(
                    t('SomeWereSkipped', { count: resp.mismatches.length }) + '\n' + resp.mismatches.join('\n'),
                    { autoClose: 8000, position: direction === 'rtl' ? 'top-left' : 'top-right' }
                );
            }
            
            setIsGeneratingDrafts(false);
            setPdfResults(resp || []);
            setPdfIndex(0);
            setPreviewOpen(true);
        } catch (error) {
            console.error(error);
            const errorMessage = error.message || "";
            
            // Always show the toast first
            const errorCodeMatch = errorMessage.match(/Code":(\d+)/); // Extract error code from JSON response
            
            if (errorCodeMatch) {
                const errorCode = errorCodeMatch[1]; // Capture the error code
                toast.error(t(`TaxInvoiceError${errorCode}`), { autoClose: false, position: direction === 'rtl' ? 'top-left' : 'top-right' }); // Use the dynamic translation key
                
                // Check if we should show the tax authority error modal for specific error codes
                if (errorCode === Constants.TaxAuthorityErrorCodes.INVOICE_NOT_APPROVED.toString() || 
                    errorCode === Constants.TaxAuthorityErrorCodes.NO_DECISION_SENT.toString()) {
                    setIsTaxAuthorityErrorModalVisible(true);
                }
            } else {
                toast.error(errorMessage, { position: direction === 'rtl' ? 'top-left' : 'top-right' }); // Fallback message if no error code found
            }
            setIsGeneratingDrafts(false);
        }
    };
    const [authUrl, setAuthUrl] = useState('');
    const [authModalVisible, setAuthModalVisible] = useState(false);

    const handleItemsSaved = async (newItems = []) => {
        try {
            // Store the items the user picked for the check currently shown
            const currentCheckId = currentAddItemsDoc?.checkId;
            const updatedItemsByCheck = {
                ...itemsByCheckId,
                [currentCheckId]: newItems
            };
            setItemsByCheckId(updatedItemsByCheck);

            // If there are more selected checks, collect items for the next one
            const nextIndex = addItemsIndex + 1;
            if (nextIndex < addItemsQueue.length) {
                setAddItemsIndex(nextIndex);
                openAddItemsForCheck(addItemsQueue, nextIndex);
                return;
            }

            // All checks have their items → generate one invoice per check
            setIsSavingItems(true);
            setShowAddItemsModal(false);
            if (!pendingPairs?.length) {
                toast.info(t('NoChecksSelected'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                return;
            }
            const pairsWithItems = pendingPairs.map(pair => ({
                ...pair,
                draftItems: updatedItemsByCheck[pair.checkId] || []
            }));
            await actuallyGenerateInvoicesDrafts(pairsWithItems);
        } catch (err) {
            console.error('Error after saving items:', err);
            toast.error(t('GenerateInvoicesFailed'), { autoClose: 5000, position: direction === 'rtl' ? 'top-left' : 'top-right' });
        } finally {
            setIsSavingItems(false);
        }
    };

    useEffect(() => {
        if (resumeInvoiceCreation) {
            handleConfirmInvoices(true);
        }
    }, [resumeInvoiceCreation]);

    function downloadBlob(blob, filename) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }

    // Opens the Tax Authority decision modal for a single rejected invoice and
    // resolves with the chosen decision flag (or null if the user dismisses it).
    const promptForInvoiceDecision = (check) => {
        return new Promise((resolve) => {
            decisionResolverRef.current = resolve;
            setDecisionCheck(check);
            setIsTaxAuthorityErrorModalVisible(true);
        });
    };

    const resolveInvoiceDecision = (value) => {
        setIsTaxAuthorityErrorModalVisible(false);
        const resolver = decisionResolverRef.current;
        decisionResolverRef.current = null;
        if (resolver) resolver(value);
    };

    const isTaxAuthorityDecisionCode = (code) =>
        String(code) === String(Constants.TaxAuthorityErrorCodes.INVOICE_NOT_APPROVED) ||
        String(code) === String(Constants.TaxAuthorityErrorCodes.NO_DECISION_SENT);

    // Generates a single postponed-check invoice. If the Tax Authority rejects it,
    // the user is prompted for a decision that applies only to THIS invoice, then
    // the invoice is retried with that decision.
    const generateSingleInvoice = async (pair) => {
        const check = checks.find(ch => ch.checkId === pair.checkId);
        let decisionFlag = null;
        let decisionAttempts = 0;
        const maxDecisionAttempts = 5;

        // retry loop: re-attempt this invoice with a decision after a rejection
        while (true) {
            try {
                const result = await DocumentService.GeneratePostponedChecks(
                    caseId,
                    [pair],
                    generateDate.toISOString(),
                    token,
                    decisionFlag
                );

                if (result.type === 'text') {
                    // e.g. a "Cancel" decision was processed – no invoice produced
                    if (result.message && result.message !== 'Decision processed successfully') {
                        toast.success(result.message, { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                    }
                    return { status: 'handled', checkId: pair.checkId };
                }

                if (result.type === 'blob') {
                    const blob = result.data;
                    const ext = blob.type === 'application/zip' ? 'zip' : 'pdf';
                    const label = check?.description || pair.checkId;
                    const fileName = `חשבונית_ציק_דחוי_${label}_${caseId}_${dayjs().format('YYYYMMDD_HHmmss')}.${ext}`;
                    downloadBlob(blob, fileName);
                    return { status: 'generated', checkId: pair.checkId };
                }

                return { status: 'handled', checkId: pair.checkId };
            } catch (err) {
                const { code } = parseServerError(err?.message || '');

                if (code && isTaxAuthorityDecisionCode(code) && decisionAttempts < maxDecisionAttempts) {
                    // Surface the error and ask for a decision for THIS invoice only
                    toast.error(t(`TaxInvoiceError${code}`), { autoClose: false, position: direction === 'rtl' ? 'top-left' : 'top-right' });
                    const chosen = await promptForInvoiceDecision(check);
                    if (chosen === null || chosen === undefined) {
                        // user dismissed the modal -> skip this invoice and move on
                        toast.info(t('GenerateInvoicesFailed'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                        return { status: 'skipped', checkId: pair.checkId };
                    }
                    decisionFlag = chosen;
                    decisionAttempts += 1;
                    continue; // retry this invoice with the chosen decision
                }

                if (code) {
                    toast.error(t(`TaxInvoiceError${code}`), { autoClose: false, position: direction === 'rtl' ? 'top-left' : 'top-right' });
                } else {
                    toast.error(t('GenerateInvoicesFailed'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                }
                return { status: 'failed', checkId: pair.checkId };
            }
        }
    };

    const handleConfirmInvoices = async (isAuthCompleted = false) => {
        setIsConfirmingFinals(true);
        setLoadingChecks(true);
        try {
            setPreviewOpen(false);
            setPdfResults([]);
            setBlobUrl('');

            if (!pendingPairs.length) return;

            // Pre-auth once if any check >= limit (token is per-case and reused
            // for every per-invoice request below)
            if (!isAuthCompleted) {
              const highAmountPairs = pendingPairs.filter(pair => {
                const check = checks.find(ch => ch.checkId === pair.checkId);
                const gross = check?.amount ?? 0;
                // The Tax Authority allocation-number requirement is measured on the
                // NET (pre-VAT) amount, exactly like the server (net = gross / vatDivider).
                // e.g. a 5200 check => net 4406.78, which is below the 5000 limit, so no
                // confirmation number is needed and we must NOT trigger the token request.
                const vatDivider = getVatValue(generateDate, true); // e.g. 1.18
                const net = vatDivider ? gross / vatDivider : gross;
                return net >= getIsraelInvoiceAmountLimit(generateDate);
              });

              if (highAmountPairs.length) {
                try {
                  const tokenData = await TaxAuthorityService.requestTokenClient(caseId, token, Constants.TaxAuthorityScopes.CreationNumber);
                  if (tokenData?.authUrl) {
                    handleTaxAuthorityAuth(tokenData.authUrl);
                    return; // stop here; user will resume after auth
                  }
                } catch (e) {
                  console.error('Error fetching token:', e);
                  toast.error(t('TaxAuthorityAuthFetchError'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                  return;
                }
              }
            }

            setLoading(true);

            // Generate every selected check on its own request so each invoice
            // gets (and applies) its own Tax Authority decision when rejected.
            let generatedCount = 0;
            for (const pair of [...pendingPairs]) {
                const res = await generateSingleInvoice(pair);
                if (res.status === 'generated') generatedCount += 1;
            }

            if (generatedCount > 0) {
                toast.success(t('InvoicesGenerated'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            }

            // cleanup once the whole batch has been processed
            setSelectedCheckIds([]);
            setPendingPairs([]);
            fetchPostponedChecks(manualChecks);
        } catch (err) {
            console.error('Error generating final invoices:', err);
            toast.error(t('GenerateInvoicesFailed'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
        } finally {
            setLoading(false);
            setLoadingChecks(false);
            setIsConfirmingFinals(false);
        }
    };
    const [client, setClient] = useState(null);
    const [type, setType] = useState('');

    /* --------------- pdf blob url maintenance --------------- */
    useEffect(() => {
        if (!pdfResults.length) {
            setBlobUrl('');
            return;
        }
        const cur = pdfResults[pdfIndex];
        if (!cur?.pdfBase64) {
            setBlobUrl('');
            return;
        }
        const url = base64ToBlobUrl(cur.pdfBase64);
        setBlobUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [pdfIndex, pdfResults]);

    /* --------------- table helpers --------------- */
    const filteredChecks = checks.filter(ch => {
        const q = searchQuery.toLowerCase();

        return (
            ch.bankName?.toLowerCase().includes(q) ||
            ch.bankBranchCode?.toLowerCase().includes(q) ||
            ch.supplierAccountNumber?.toLowerCase().includes(q) ||
            ch.description?.toLowerCase().includes(q)
        );
    });

    const sortedChecks = [...filteredChecks].sort((a, b) => {
        const { key, direction } = sortConfig;
        let aVal = a[key];
        let bVal = b[key];
        if (key === 'amount') { aVal = +aVal || 0; bVal = +bVal || 0; }
        else if (key === 'paymentDate') { aVal = dayjs(aVal); bVal = dayjs(bVal); }
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        return aVal < bVal ? (direction === 'asc' ? -1 : 1) :
            aVal > bVal ? (direction === 'asc' ? 1 : -1) : 0;
    });

    const paginatedChecks = sortedChecks.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    const getAccountDisplay = (id) => {
        if (!id) return '-';
        const acc = accountCards?.find((a) => a.cardId === id);
        return acc ? `${acc.cardId} – ${acc.name || ''}` : id;
    };
    /* ---------- PA‑modal local editing state ---------- */
    const [palCode, setPalCode] = useState('');
    const [statusCheck, setStatusCheck] = useState(false);   // processed / not‑processed
    const [buttonDisabled, setButtonDisabled] = useState(false);

    /* the fields below live inside editablePA,  
       but we keep tiny helpers so we can feed the modal simple callbacks */
    const setPaymentDate = (d) => setEditablePA(p => ({ ...p, date: d }));
    const setReportingMonthDate = (d) =>
        setEditablePA(p => ({ ...p, reportingMonthDate: d }));
    const handleDescriptionChange = (e) =>
        setEditablePA(p => ({ ...p, description: e.target.value }));
    const handleChangeOriginalTaxClearance = (e) =>
        setEditablePA(p => ({ ...p, withholdingTax: +e.target.value || 0 }));

    /* every field‑level “small” change from the modal */
    const handleAddEditableItemChange = (field, value) =>
        setEditablePA(p => ({ ...p, [field]: value }));


    /* table‑style item editing inside the modal */
    const handleItemChange = (type, index, field, value) =>
        setEditablePA(p => {
            const arr = [...p[type]];
            arr[index] = { ...arr[index], [field]: value };
            return { ...p, [type]: arr };
        });
    const handleRemoveItem = (type, index) =>
        setEditablePA(p => {
            const arr = p[type].filter((_, i) => i !== index);
            return { ...p, [type]: arr };
        });

    /* radio button inside modal */
    const handleRadioChange = (e) => setStatusCheck(e.target.value === 'done');

    /* add a new empty “row” inside the modal */
    const handleAddItem = (theType, suggestedAmount = '', accountFields = {}) =>
        setEditablePA(p => ({
            ...p,
            [theType]: [...(p[theType] || []), {
                type: theType,
                amount: suggestedAmount,
                paymentDate: new Date(),
                ...accountFields,
            }],
        }));


    const openPaymentAcceptanceModal = (check) => {
        const pa = paymentAcceptances.find(p => p.id === check.paymentAcceptanceId);
        if (!pa) return;

        const paForModal = {
            ...pa,
            checks: pa.checks || [],
            creditCards: pa.creditCards || [],
            cash: pa.cash || [],
            bankTransactions: pa.bankTransactions || [],
            paymentAcceptanceType: pa.paymentAcceptanceType,
            docType: 'PaymentAcceptances'
        };
        setEditablePA(paForModal);
        setPalCode(paForModal.palCode || '');
        setStatusCheck(paForModal.status === 'done');
        setButtonDisabled(false);
        setPaModalOpen(true);
    };


    const handleMuiChange = (event, value) => {
        const updatedFields = {
            accountCaseId: value.accountCaseId,
            accountCodeId: value.cardId,
            accountCodeName: value.name,
        };

        // setEditablePA(p => ({ ...p, [field]: value }));
        setEditablePA(prevState => ({
            ...prevState,
            ...updatedFields,
        }));
        setSelectedOption({ ...value });

        updateAllItems(value, updatedFields);

    };

    //--------------------------------//
    //   payment acceptance edit 
    //--------------------------------//

    const [loading, setLoading] = useState(false);


    const [isDataFetched, setIsDataFetched] = useState(false);

    const [formattedPalCodeOptions, setFormattedPalCodeOptions] = useState([]);
    //for item edit
    const [editedItem, setEditedItem] = useState({});
    const [editingRow, setEditingRow] = useState(null);
    const [editedAddAccountCard, setEditedAddAccountCard] = useState(false);
    const [accountCardOptions, setAccountCardOptions] = useState([]);
    const [isClosedReportingMonth, setIsClosedReportingMonth] = useState(false);
    const [sortCodeIdOptions, setSortCodeIdOptions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(-1);

    const [fade, setFade] = useState(false);
    const [editedPopupItem, setEditedPopupItem] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const [isRange, setIsRange] = useState(() => {
        const storedIsRange = localStorage.getItem('IsRange');
        return storedIsRange === "true";
    });
    const handleCancel = () => {
        setEditingRow(null);
        setEditedItem({});
        setEditedAddAccountCard(false);
    };



    useEffect(() => {
        if (currentIndex != -1) {
            setFade(true);
            const timer = setTimeout(() => setFade(false), 500);
            return () => clearTimeout(timer);
        }
    }, [currentIndex]);




    const handleDoneClick = async (itemId, docType) => {
        setLoading(true);
        try {
            const response = await ClientDocumentCountService.removeDocumentByTypeAndId(caseId, client.accountantId, docType, itemId, token)
            if (response.ok) {
                await fetchDocumentsNotification(client, token)
                toast.success(t('DocumentProcessed'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            } else {
                const errorText = await response.text();
                toast.error(t('DocumentNotProcessed', { errorText }), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            }
        } catch (error) {
            toast.error(t('DocumentNotProcessed', { errorText: error.message }), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
        }
        setLoading(false);
    };
    const handleSortChange = (key, type, Direction = '') => {
        let direction = Direction || 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction, type });
    };

    const fetchDocumentsNotification = async (client, token) => {
        const documentCounts = await ClientDocumentCountService.getClientDocumentCountByCaseId(caseId, token);
        // Enrich clients with their total document counts
        const enrichedClient = {
            ...client,
            totalCounts: documentCounts.totalCounts || 0, // Default to 0 if no matching count is found
            documentCounts: documentCounts.documentCounts || {},
            documentIds: documentCounts.documentIds || {},
        };
        setClient(enrichedClient);
    }
    const handleNotDoneClick = async (itemId, docType) => {
        setLoading(true);
        try {
            const response = await ClientDocumentCountService.addDocumentByTypeAndId(caseId, client.accountantId, docType, itemId, token)
            if (response.ok) {
                await fetchDocumentsNotification(client, token)
                toast.success(t('DocumentProcessed'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            } else {
                const errorText = await response.text();
                toast.error(t('DocumentNotProcessed', { errorText }), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            }
        } catch (error) {
            toast.error(t('DocumentNotProcessed', { errorText: error.message }), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
        }
        setLoading(false);
    };
    const handleAccountCardSave = async (AccountCodeId, AccountCodeName, AccountCaseId) => {
        const response2 = await EnumsService.AddAccountCard(caseId, AccountCodeId, AccountCodeName, AccountCaseId, token);
        if (!response2.ok) {
            throw new Error(`HTTP error! status: ${response2.status}`);
        }
    };


    //FetchEnums
    const fetchEnums = async (user, token, client) => {
        setLoading(true);
        try {
            // Fetching sortCodeIdOptions
            // Fetching creditAccountOptions and debitAccountOptions
            const accountsResponse = await EnumsService.GetAccountCardsByCaseId(caseId, token);

            if (accountsResponse.ok) {
                const accountsData = await accountsResponse.json();
                const accountCardOptions = [{ name: t('AddAccountCard'), value1: 'AddItem' }, ...accountsData];


                setAccountCardOptions(accountCardOptions);
            }
            const palCodesResponse = await EnumsService.GetPalCodesByAccountantId(client.accountantId, token)
            // Process palCodes if response is okay
            if (palCodesResponse.ok) {
                const palCodesData = await palCodesResponse.json();
                const formattedPalCodes = palCodesData.map(palCode => ({
                    label: palCode.palCodeId,
                    value: palCode.palCodeId
                }));


                setFormattedPalCodeOptions(formattedPalCodes);
            }

            setIsDataFetched(true);
        } catch (error) {
            console.error("Error fetching enum data:", error);
            toast.error(`${t("Error fetching enum data")}: ${error}`, { position: direction === 'rtl' ? 'top-left' : 'top-right' });
        }
        setLoading(false);
    };


    //payment functions
    const [addEditableItem, setAddEditableItem] = useState({
        checks: [],
        creditCards: [],
        cash: [],
        bankTransactions: [],
        date: new Date()
    });
    const [modalVisible, setModalVisible] = useState(false);
    const [addNewAccountCardDocument, setAddNewAccountCardDocument] = useState(false);
    const [selectedOption, setSelectedOption] = useState("");
    const isClosedReportingMonthAndIsRange = !isRange ? isClosedReportingMonth : addEditableItem.isClosedReportingMonth;
    const [accountCodeId, setAccountCodeId] = useState('');
    const [accountType, setAccountType] = useState(null);
    const [actionType, setActionType] = useState('delete');
    const [accountCardIdToDelete, setAccountCardIdToDelete] = useState('');
    const [paymentBeingEdited, setPaymentBeingEdited] = useState(true);


    const CustomPalCode = (props) => {
        return (
            <div style={{ textAlign: 'center', width: '100%' }}>{props.label}</div>
        );
    };

    const onRefresh = async () => {
        await fetchPostponedChecks();
    };

    const handleChangeAccountType = (selectedOption) => {
        setAccountType(selectedOption.value);
        setAddEditableItem((prevState) => ({
            ...prevState,
            paymentAcceptanceType: selectedOption ? selectedOption.value : ''
        }));
    };

    useEffect(() => {
        let unionArray = [];
        if (addEditableItem) {
            unionArray = [
                ...(addEditableItem.checks || []),
                ...(addEditableItem.creditCards || []),
                ...(addEditableItem.cash || []),
                ...(addEditableItem.bankTransactions || [])
            ];
        }

        if (unionArray.length > 0) {
            const allAmountsFilled = unionArray.every(item => item.amount !== undefined && item.amount !== null && item.amount !== '');
            const allFieldsFilled = allAmountsFilled &&
                (addEditableItem.accountCaseId !== undefined && addEditableItem.accountCaseId !== null && addEditableItem.accountCaseId !== '' ||
                    addEditableItem.accountCodeId !== undefined && addEditableItem.accountCodeId !== null && addEditableItem.accountCodeId !== '' ||
                    addEditableItem.accountCodeName !== undefined && addEditableItem.accountCodeName !== null && addEditableItem.accountCodeName !== '') &&
                addEditableItem.paymentAcceptanceType !== undefined && addEditableItem.paymentAcceptanceType !== null && addEditableItem.accountType !== '';
        } else {
            setButtonDisabled(false);
        }
    }, [addEditableItem, accountCodeId, accountType]);

    const setCurrentItem = async (transaction, rowIndex) => { return }

    const handleEdit = (transaction, index) => {
        // setCurrentItem(transaction, index);
    };


    const updateAllItems = (value, updatedFields) => {
        const updatedItems = ['checks', 'bankTransactions', 'creditCards', 'cash'];
        const paymentType = addEditableItem.paymentAcceptanceType;

        updatedItems.forEach(itemType => {
            addEditableItem[itemType]?.forEach((item, index) => {
                // Determine the field to update
                const fieldToUpdate = paymentType === 'תקבול' ? 'creditAccount' : 'debitAccount';

                handleItemChange(itemType, index, fieldToUpdate, value.cardId);
            });
        });

        // Update addEditableItem with new account data
        setEditablePA(prevState => ({
            ...prevState,
            ...updatedFields,
        }));
    };

    const handleAccountDelete = (option) => {
        setActionType('deleteAccountCard');
        setAccountCardIdToDelete(option.cardId);
        setIsModalVisible(true);
        // handleAccountCardDeleteClick(option.cardId);
    };

    const handleClose = async (clearDocuments = true) => {
        setAddEditableItem({
            checks: [],
            creditCards: [],
            cash: [],
            bankTransactions: [],
            date: new Date()
        });
    }

    const getItemAtIndex = (transactions, newIndex) => {
        // Convert the object to an array of its values
        const transactionsArray = Object.values(transactions);

        // Check if the index is within the bounds of the array
        if (newIndex >= 0 && newIndex < transactionsArray.length) {
            return transactionsArray[newIndex];
        } else {
            console.error('Index out of bounds');
            return null;
        }
    };

    const [palCodeDetails, setPalCodeDetails] = useState('');


    const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
        <button className={`document-custom-input document-custom-input-range `} onClick={onClick} ref={ref}>
            {value}
        </button>
    ));



    const checkAuthCompletion = async (authWindow) => {
        setLoading(true);
        try {
            let attempts = 0;
            const maxAttempts = 25;
            while (attempts < maxAttempts) {
                // Check if the authWindow was closed by the user
                if (authWindow.closed) {
                    setLoading(false);
                    toast.error(t('Authentication window was closed by the user.'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                    return;
                }

                const result = await TaxAuthorityService.checkAuthCompletionClient(caseId, Constants.TaxAuthorityScopes.CreationNumber);
                if (result.completed) {
                    setLoading(false);
                    setResumeInvoiceCreation(true);
                    // Close the auth window
                    if (authWindow && !authWindow.closed) {
                        authWindow.close();
                    }
                    return;
                } else {
                    attempts += 1;
                    await new Promise((resolve) => setTimeout(resolve, 3000));
                }
            }
            setLoading(false);
            toast.error(t('tokenFetchError'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            // Close the auth window on timeout
            if (authWindow && !authWindow.closed) {
                authWindow.close();
            }
        } catch (error) {
            console.error('Error checking auth completion:', error);
            setLoading(false);
            toast.error(t('tokenFetchError'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            // Close the auth window on error
            if (authWindow && !authWindow.closed) {
                authWindow.close();
            }
        }
    };
    const [authWindow, setAuthWindow] = useState(null);

    const handleTaxAuthorityAuth = (authUrl) => {
        // Open the authentication URL in a new tab

        const authWindow = window.open(authUrl, '_blank');

        // Store the authWindow in state
        setAuthWindow(authWindow);

        // Start checking for authentication completion
        checkAuthCompletion(authWindow);
    };

    const handleDeleteClick = () => {
        setIsModalVisible(true);
    };

    const handleEditDeleteClick = (itemId) => {
        setEditedPopupItem(false);
        setEditedItem({});
        setAddEditableItem({
            checks: [],
            creditCards: [],
            cash: [],
            bankTransactions: [],
            date: new Date()
        });
        handleDeleteClick();
    };

    const customScrollbarRef = useRef();

    const filteredAndSortedTransactions = useMemo(() => { return });

    const handlePrevious = () => {
        let newIndex = currentIndex;

        while (newIndex > 0) {
            newIndex -= 1;
            const previousItem = getItemAtIndex(filteredAndSortedTransactions, newIndex);
            const currentItem = getItemAtIndex(filteredAndSortedTransactions, currentIndex);

            // Check if the previous item has a different id
            if (previousItem.id !== currentItem.id) {
                setCurrentItem(previousItem, newIndex);
                break;  // Exit the loop once a different ID is found
            }
        }
    };

    const handleNext = () => {
        let newIndex = currentIndex;

        while (newIndex < filteredAndSortedTransactions.length - 1) {
            newIndex += 1;
            const nextItem = getItemAtIndex(filteredAndSortedTransactions, newIndex);
            const currentItem = getItemAtIndex(filteredAndSortedTransactions, currentIndex);
            // Check if the next item has a different id
            if (nextItem.id !== currentItem.id) {
                setCurrentItem(nextItem, newIndex);
                break;  // Exit the loop once a different ID is found
            }
        }
    };

    /* --------------- render --------------- */

    return (
        <>
            <div className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}>
                <div className={styles.modal} dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.body}>
                    <div className={styles.header}>
                        <Tooltip title={t('Close')}>
                            <IconButton onClick={onClose} className={styles.closeIcon}>
                                <CancelIcon className={styles.closeIcon} />
                            </IconButton>
                        </Tooltip>
                        <div>{t('PostponedChecks')}</div>
                        <div />
                    </div>

                    <div className={`${styles.toolbar} ${direction === 'rtl' ? fieldStyles.rtl : fieldStyles.ltr}`}>
                        <div className={styles.searchField} dir={direction}>
                            <span className={styles.fieldLabel}>{t('Search')}</span>
                            <TextField
                                placeholder={t('Search')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                variant="standard"
                                className={fieldStyles.styleInput}
                                InputProps={searchInputProps}
                                inputProps={{
                                    style: { textAlign: direction === 'rtl' ? 'right' : 'left' },
                                }}
                                fullWidth
                            />
                        </div>

                        <div className={styles.dateField} dir={direction}>
                            <span className={styles.fieldLabel}>{t('StartDate')}</span>
                            <CustomDatePicker
                                date={startDate}
                                onDateChange={setStartDate}
                                height="40px"
                                clearable
                                textFieldClassName={fieldStyles.styleInput}
                                inputSx={datePickerInputSx}
                            />
                        </div>

                        <div className={styles.dateField} dir={direction}>
                            <span className={styles.fieldLabel}>{t('EndDate')}</span>
                            <CustomDatePicker
                                date={untilDate}
                                onDateChange={setUntilDate}
                                height="40px"
                                textFieldClassName={fieldStyles.styleInput}
                                inputSx={datePickerInputSx}
                            />
                        </div>

                        <div className={styles.dateField} dir={direction}>
                            <span className={styles.fieldLabel}>{t('GenerateInvoicesForDate')}</span>
                            <CustomDatePicker
                                date={generateDate}
                                onDateChange={setGenerateDate}
                                height="40px"
                                textFieldClassName={fieldStyles.styleInput}
                                inputSx={datePickerInputSx}
                            />
                        </div>

                        <div className={styles.toolbarEnd}>
                            {!manualChecks ? (
                                <button type="button" className={styles.ghostBtn} onClick={() => handleChangeManualChecks(true)}>
                                    {t('ViewCloseChecks')}
                                </button>
                            ) : (
                                <button type="button" className={styles.ghostBtn} onClick={() => handleChangeManualChecks(false)}>
                                    {t('Return')}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={styles.tableWrap}>
                        <TableContainer component={Paper} dir={direction}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" className={styles.selectCell}>
                                            <Checkbox
                                                color="primary"
                                                checked={
                                                    paginatedChecks.length &&
                                                    paginatedChecks.every(ch => selectedCheckIds.includes(ch.checkId))
                                                }
                                                onChange={() => {
                                                    const ids = paginatedChecks.map(c => c.checkId);
                                                    setSelectedCheckIds(prev =>
                                                        ids.every(id => prev.includes(id))
                                                            ? prev.filter(id => !ids.includes(id))
                                                            : [...new Set([...prev, ...ids])]
                                                    );
                                                }}
                                                sx={{ color: '#304fff', '&.Mui-checked': { color: '#304fff' } }}
                                            />
                                            {t('SelectAll')}
                                        </TableCell>
                                        {['bankName', 'bankCode', 'bankBranchCode', 'BankAccountNumber', 'paymentDate', 'CheckNumber', 'amount'].map(col => (
                                            <TableCell
                                                key={col}
                                                onClick={() =>
                                                    setSortConfig(s => ({
                                                        key: col,
                                                        direction: s.key === col && s.direction === 'asc' ? 'desc' : 'asc'
                                                    }))
                                                }
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {t(col === 'paymentDate' ? 'DuePaymentDate' :
                                                    col === 'bankBranchCode' ? 'bankBranchCode' :
                                                        col.charAt(0).toUpperCase() + col.slice(1))}
                                            </TableCell>
                                        ))}
                                        <TableCell>{t('Credit')}</TableCell>
                                        <TableCell>{t('Debit')}</TableCell>
                                        <TableCell>{t('Notes')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loadingChecks ? (
                                        <TableRow>
                                            <TableCell colSpan={12}>
                                                <div className={styles.emptyState}>
                                                    <CircularProgress className={styles.spinner} size={28} />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : paginatedChecks.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={12}>
                                                <div className={styles.emptyState}>{t('NoData')}</div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedChecks.map(ch => {
                                            const {
                                                checkId, bankName, bankCode, bankBranchCode,
                                                supplierAccountNumber, paymentDate, description,
                                                amount, creditAccount, debitAccount
                                            } = ch;

                                            const isSelected = selectedCheckIds.includes(checkId);
                                            const currentNote = notes[checkId] || '';
                                            const isEditing = editingCheckId === checkId;

                                            return (
                                                <TableRow
                                                    key={checkId}
                                                    hover
                                                    selected={isSelected}
                                                    onDoubleClick={() => openPaymentAcceptanceModal(ch)}
                                                >
                                                    <TableCell padding="checkbox" className={styles.selectCell} onClick={(e) => e.stopPropagation()}>
                                                        <Checkbox
                                                            color="primary"
                                                            checked={isSelected}
                                                            onChange={() => setSelectedCheckIds(p =>
                                                                p.includes(checkId)
                                                                    ? p.filter(x => x !== checkId)
                                                                    : [...p, checkId]
                                                            )}
                                                            sx={{ color: '#304fff', '&.Mui-checked': { color: '#304fff' } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{bankName || '-'}</TableCell>
                                                    <TableCell>{bankCode || '-'}</TableCell>
                                                    <TableCell>{bankBranchCode || '-'}</TableCell>
                                                    <TableCell>{supplierAccountNumber || '-'}</TableCell>
                                                    <TableCell>
                                                        {paymentDate ? dayjs(paymentDate).format('YYYY-MM-DD') : '-'}
                                                    </TableCell>
                                                    <TableCell>{description || '-'}</TableCell>
                                                    <TableCell>{amount || 0}</TableCell>
                                                    <TableCell>{getAccountDisplay(creditAccount)}</TableCell>
                                                    <TableCell>{getAccountDisplay(debitAccount)}</TableCell>
                                                    <TableCell
                                                        onClick={(e) => { e.stopPropagation(); setEditingCheckId(checkId); }}
                                                        sx={{
                                                            cursor: 'pointer', maxWidth: 220, whiteSpace: 'nowrap',
                                                            overflow: 'hidden', textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {isEditing ? (
                                                            <TextField
                                                                autoFocus
                                                                variant="standard"
                                                                className={fieldStyles.styleInput}
                                                                value={currentNote}
                                                                onChange={e => setNotes(p => ({ ...p, [checkId]: e.target.value }))}
                                                                onBlur={() => setEditingCheckId(null)}
                                                                onKeyDown={e => e.key === 'Enter' && setEditingCheckId(null)}
                                                                InputProps={searchInputProps}
                                                                fullWidth
                                                            />
                                                        ) : currentNote || <span className={styles.notePlaceholder}>{t('ClickToAddNote')}</span>}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>

                    <div className={styles.pagination}>
                        <TablePagination
                            component="div"
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            count={sortedChecks.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
                            labelRowsPerPage={t('RowsPerPage')}
                        />
                    </div>

                    <div className={styles.footer}>
                        {!manualChecks ? (
                            <button
                                type="button"
                                className={styles.secondaryBtn}
                                disabled={!selectedCheckIds.length}
                                onClick={handleCloseManually}
                            >
                                {t('CloseManually')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={styles.secondaryBtn}
                                disabled={!selectedCheckIds.length}
                                onClick={handleOpenManually}
                            >
                                {t('Reopen')}
                            </button>
                        )}
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            disabled={isGeneratingDrafts}
                            onClick={handleGenerateInvoicesDrafts}
                        >
                            {isGeneratingDrafts ? `…${t('Generating')}` : t('GenerateInvoices')}
                        </button>
                    </div>
                    </div>
                </div>

                {previewOpen && (
                    <div style={{
                        position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.67)', zIndex: 3000,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16
                    }}>
                        <div style={{
                            backgroundColor: '#FFF', width: '30%', height: '80%', position: 'relative',
                            borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column',
                            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)', padding: 16
                        }}>
                            <div style={{
                                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid #E0E0E0', borderRadius: 8, overflow: 'hidden'
                            }}>
                                <AnimatePresence mode="wait">
                                    {blobUrl && (
                                        <motion.iframe
                                            key={blobUrl}
                                            src={blobUrl}
                                            title={pdfResults[pdfIndex]?.fileName || 'Draft PDF'}
                                            initial={{ opacity: 0, x: slideDirection * 100 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: slideDirection * -100 }}
                                            transition={{ duration: 0.1 }}
                                            style={{ width: '100%', height: '100%', border: 'none' }}
                                        />
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>

                        <div style={{
                            marginTop: 16, display: 'flex', justifyContent: 'space-between', width: '42%',
                            position: 'relative', alignItems: 'center', top: '-400px'
                        }}>
                            {/* Previous Button */}
                            {pdfIndex > 0 && (
                                <div style={{
                                    position: 'absolute', left: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 50, height: 50, top: '10%', borderRadius: '50%', backgroundColor: '#FFF', cursor: 'pointer'
                                }}
                                    onClick={() => { setSlideDirection(-1); setPdfIndex(i => Math.max(0, i - 1)); }}>
                                    <ArrowBackIcon style={{ color: '#304FFF' }} />
                                </div>
                            )}

                            {/* Next Button */}
                            {pdfIndex < pdfResults.length - 1 && (
                                <div style={{
                                    position: 'absolute', right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 50, height: 50, top: '10%', borderRadius: '50%', backgroundColor: '#FFF', cursor: 'pointer'
                                }}
                                    onClick={() => { setSlideDirection(1); setPdfIndex(i => Math.min(pdfResults.length - 1, i + 1)); }}>
                                    <ArrowForwardIcon style={{ color: '#304FFF' }} />
                                </div>
                            )}
                        </div>

                        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
                            <button
                                type="button"
                                className={styles.dangerBtn}
                                onClick={() => {
                                    setPreviewOpen(false);
                                    setPdfResults([]);
                                    setBlobUrl('');
                                }}
                            >
                                <CloseIcon style={{ fontSize: 18 }} />
                                {t('Cancel')}
                            </button>
                            <button
                                type="button"
                                className={styles.primaryBtn}
                                onClick={() => handleConfirmInvoices()}
                            >
                                <CheckIcon style={{ fontSize: 18 }} />
                                {t('OK')}
                            </button>
                        </div>
                    </div>
                )}

                {/* missing phones modal (unchanged) */}
                {missingPhonesModalOpen && (
                    <div style={{
                        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3001,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: 25
                    }}>
                        <div style={{ backgroundColor: '#FFF', padding: 16, borderRadius: 8, width: 400 }}>
                            <h3>{t('MissingPhonesTitle')}</h3>
                            <p>{t('MissingPhonesDescription')}</p>
                            {missingPhones.map(item => (
                                <div key={item.cardId} style={{ marginBottom: 8, padding: 10 }}>
                                    <div>
                                        <strong>{item.name}</strong> ({t('Number')}: {item.cardId})
                                    </div>
                                    <TextField
                                        placeholder={t('EnterPhoneNumber')}
                                        value={phoneEdits[item.cardId] || ''}
                                        onChange={e => setPhoneEdits(prev => ({ ...prev, [item.cardId]: e.target.value }))}
                                        variant="standard"
                                        style={{ width: '100%' }}
                                        inputProps={{ style: { textAlign: 'right' } }}
                                    />
                                </div>
                            ))}

                            <div className={styles.footer} style={{ marginTop: 16, paddingTop: 12 }}>
                                <button
                                    type="button"
                                    className={styles.secondaryBtn}
                                    onClick={() => setMissingPhonesModalOpen(false)}
                                >
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="button"
                                    className={styles.primaryBtn}
                                    onClick={async () => {
                                    try {
                                        for (const m of missingPhones) {
                                            const newPhone = phoneEdits[m.cardId];
                                            if (newPhone) {
                                                await DocumentService.UpdateAccountCardPhone(
                                                    m.cardId, caseId, newPhone, token);
                                            }
                                        }
                                        toast.success(t('PhoneNumbersUpdated'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                                        await fetchAccountCards();
                                        setMissingPhonesModalOpen(false);
                                    } catch (err) {
                                        console.error('Error saving phone numbers:', err);
                                        toast.error(t('CouldNotSavePhones'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
                                    }
                                }}
                                >
                                    {t('Save')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* -------- PaymentAcceptance edit modal -------- */}
                {paModalOpen && editablePA && (
                    <PaymentAcceptanceCreateEditModal
                        openInvoiceCreatorModal={paModalOpen}
                        handleClose={closePaModal}
                        t={t}
                        /* basic meta */
                        itemEditing={true}
                        Constants={Constants}
                        direction={direction}
                        /* data */
                        addEditableItem={editablePA}
                        palCode={palCode}
                        isRange={false}
                        isClosedReportingMonth={false}
                        accountCardOptions={accountCards}
                        /* field‑level state setters */
                        setPalCode={setPalCode}
                        setPaymentDate={setPaymentDate}
                        setReportingMonthDate={setReportingMonthDate}
                        handleDescriptionChange={handleDescriptionChange}
                        handleChangeOriginalTaxClearance={handleChangeOriginalTaxClearance}
                        handleAddEditableItemChange={handleAddEditableItemChange}
                        handleItemChange={handleItemChange}
                        handleRemoveItem={handleRemoveItem}
                        setLoading={setLoading}
                        /* processing / status */
                        statusCheck={statusCheck}
                        handleRadioChange={handleRadioChange}
                        /* add / remove sub‑rows */
                        handleAddItem={handleAddItem}
                        /* save */
                        handleSave={handleSavePaymentAcceptance}          // you can call your own save later
                        buttonDisabled={buttonDisabled}
                        /* unused but required by modal’s prop‑types */
                        addNewAccountCardDocument={false}
                        handleMuiChange={handleMuiChange}
                        handleEdit={handleEdit}
                        handleChangeAccountType={handleChangeAccountType}
                        paymentBeingEdited={paymentBeingEdited}
                        CustomDropdown={CustomDropdown}
                        setAddNewAccountCardDocument={setAddNewAccountCardDocument}
                        fetchEnums={fetchEnums}
                        isClosedReportingMonthAndIsRange={isClosedReportingMonthAndIsRange}
                        CustomPalCode={CustomPalCode}
                        handlePrevious={handlePrevious}
                        currentIndex={0}
                        filteredAndSortedData={[]}
                        handleNext={handleNext}
                        handleDeleteClick={handleEditDeleteClick}
                        token={token}
                        client={client}
                        handleTransferRefresh={async () => {
                            await fetchPostponedChecks();
                        }}
                    />

                )}
                {showAddItemsModal && (
                    <AddItemsModal
                        key={currentAddItemsDoc?.checkId}
                        open={showAddItemsModal}
                        onClose={resetAddItemsFlow}
                        onSave={handleItemsSaved}
                        existingItems={[]}
                        items={clientItems}
                        doc={currentAddItemsDoc}
                        headerNote={
                            currentAddItemsDoc?.total > 1
                                ? `${t('CheckNumber')} ${currentAddItemsDoc?.checkLabel || ''} (${currentAddItemsDoc?.position}/${currentAddItemsDoc?.total})`
                                : ''
                        }
                        pendingAction={null}
                    />
                )}
                {isSavingItems && (
                    <Box
                        sx={{
                            position: 'fixed',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 4000,
                            pointerEvents: 'none',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
            </div>

            {/* Per-invoice Tax Authority decision modal (one invoice at a time) */}
            <TaxAuthorityErrorModal
                key={decisionCheck?.checkId || 'none'}
                open={isTaxAuthorityErrorModalVisible}
                onClose={() => resolveInvoiceDecision(null)}
                onOptionSelect={(decisionFlag) => resolveInvoiceDecision(decisionFlag)}
                invoiceData={{
                    invoiceId: decisionCheck?.checkId || "0"
                }}
                invoiceLabel={
                    decisionCheck
                        ? `${t('CheckNumber')}: ${decisionCheck.description || decisionCheck.checkId} • ${t('Amount')}: ${decisionCheck.amount ?? ''} • ${t('Date')}: ${decisionCheck.paymentDate ? dayjs(decisionCheck.paymentDate).format('DD/MM/YYYY') : ''}`
                        : ''
                }
                caseId={caseId || "0"}
                token={token}
                isInvoiceReceipt = {true}
            />
        </>
    );
};

export default PostponedChecks;
